import { useEffect } from 'react';
import LoadingScreen from '@/components/feature/LoadingScreen';
import Header from '@/components/feature/Header';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import MobileCTA from '@/components/feature/MobileCTA';
import Footer from '@/components/feature/Footer';
import InvestmentBenefits from './components/InvestmentBenefits';
import Hero from './components/Hero';
import ProjectOverview from './components/ProjectOverview';
import WhyMalur from './components/WhyMalur';
import Amenities from './components/Amenities';
import Testimonials from './components/Testimonials';
import Location from './components/Location';
import NearbyInstitutions from './components/NearbyInstitutions';
import WhatsAppForm from './components/WhatsAppForm';
import CTASection from './components/CTASection';
import ContactSection from './components/ContactSection';

const SCHEMA_LD = [
  {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: 'Confident Auriga – Premium Plots Near Bangalore',
    description:
      'Confident Auriga is a premium 90-acre gated plotted development near Bangalore in Malur. Explore 1100+ premium plots with clubhouse, parks, landscaped gardens, wide roads, and excellent connectivity to Bangalore, VIT University, Garden City University, and Sharanya Narayani International School.',
    url: `${import.meta.env.VITE_SITE_URL ?? 'https://www.confidentauriga.com'}/`,
    image: `${import.meta.env.VITE_SITE_URL ?? 'https://www.confidentauriga.com'}/og-image.jpg`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Promenade Rd, Thornahalli',
      addressLocality: 'Malur',
      addressRegion: 'Karnataka',
      postalCode: '563130',
      addressCountry: 'IN',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 13.0012,
      longitude: 77.9384,
    },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: 'Confident Group',
      },
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://www.confidentauriga.com'}/#localbusiness`,
    name: 'Confident Auriga',
    description:
      'Premium 90-acre gated plotted development near Bangalore in Malur, Karnataka. 1100+ premium plots with clubhouse, parks, landscaped gardens, wide roads, and excellent connectivity to Bangalore.',
    url: `${import.meta.env.VITE_SITE_URL ?? 'https://www.confidentauriga.com'}/`,
    telephone: '+918861133745',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Promenade Rd, Thornahalli',
      addressLocality: 'Malur',
      addressRegion: 'Karnataka',
      postalCode: '563130',
      addressCountry: 'IN',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 13.0012,
      longitude: 77.9384,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '09:00',
        closes: '18:00',
      },
    ],
    sameAs: ['https://maps.app.goo.gl/tmtvXkDok5HmQgH17'],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Where is Confident Auriga located?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Confident Auriga is located at Promenade Rd, Thornahalli, Malur, Karnataka 563130 — approximately 50 km from Bangalore on the Bangalore–Chennai corridor, with excellent connectivity to Bangalore.',
        },
      },
      {
        '@type': 'Question',
        name: 'How many premium plots near Bangalore are available in Confident Auriga?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Confident Auriga offers 1100+ premium plots near Bangalore spread across 90 acres of gated community development in Malur, Karnataka.',
        },
      },
      {
        '@type': 'Question',
        name: 'What amenities does Confident Auriga offer?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Confident Auriga features a clubhouse, parks, landscaped gardens, wide roads, jogging track, children\'s play area, 24/7 security, and proximity to VIT University, Garden City University, and Sharanya Narayani International School.',
        },
      },
      {
        '@type': 'Question',
        name: 'How can I book a site visit to Confident Auriga plotted development near Bangalore?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'You can book a site visit to this investment plots near Bangalore by calling +91 88611 33745 or by sending a WhatsApp enquiry through this website.',
        },
      },
      {
        '@type': 'Question',
        name: 'Which universities and schools are near Confident Auriga in Malur?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Confident Auriga in Malur is located near VIT University, Garden City University, and Sharanya Narayani International School, making it an ideal villa plots in Malur for families and investors.',
        },
      },
    ],
  },
];

export default function Home() {
  useEffect(() => {
    const existing = document.getElementById('schema-ld');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'schema-ld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(SCHEMA_LD);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-ld');
      if (el) el.remove();
    };
  }, []);

  return (
    <main className="relative">
      <LoadingScreen />
      <Header />
      <Hero />
      <ProjectOverview />
      <WhyMalur />
      <Amenities />
      <InvestmentBenefits />
      <Testimonials />
      <Location />
      <NearbyInstitutions />
      <WhatsAppForm />
      <CTASection />
      <ContactSection />
      <Footer />
      <FloatingWhatsApp />
      <MobileCTA />
    </main>
  );
}